import { Breadcrumbs, Typography } from '@mui/material';
import { EEntityType, ELinks, entityTypeToLink } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import getSkillCategory from '@src/APIs/graphQl/SkillCategory/getSkillCategory';
import EmployeesCard from '@src/App/Components/Cards/EntityCards/Employees/EmployeesCard/EmployeesCard';
import SkillCategoriesCard from '@src/App/Components/Cards/EntityCards/SkillCategories/SkillCategoriesCard/SkillCategoriesCard';
import SkillsCard from '@src/App/Components/Cards/EntityCards/Skills/SkillsCard/SkillsCard';
import EntityPageInformation from '@src/App/Components/Core/Layouts/LayoutElements/EntityPageInformation/EntityPageInformation';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import SkillCategoryDialog from '@src/App/Components/Dialogs/SkillCategoryDialog/SkillCategoryDialog';
import ExpandableText from '@src/App/Components/General/ExpandableText/ExpandableText';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { SkillCategory } from '@src/Objects/SkillCategory';
import { updateSkillCategory } from '@store/reducer/skillCategoriesReducer';
import type { AppState } from '@store/store';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';

import EntityPage from '../../Generic/EntityPage/EntityPage';

interface SkillCategoryPageProps {
    entity?: SkillCategory;
    inDialog?: boolean;
}

/**
 * SkillCategoryPage component.
 *
 * @param props - The props for the SkillCategoryPage component.
 * @returns The JSX.Element representing the SkillCategoryPage component.
 */
function SkillCategoryPage(props: SkillCategoryPageProps) {
    const dispatch = useDispatch();
    const { openDialog } = useDialog();
    const { id } = useParams();
    const entityId = props.entity ? props.entity.id : id ? Number.parseInt(id) : -1;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).skillCategories;
    const entityState = useSelector((state: AppState) => state.skillCategories.find((a) => a.id === entityId));

    const skillCategories = useSelector((state: AppState) => state.company.skillCategories);
    const allSkills = useSelector((state: AppState) => state.company.allSkills);
    const permissions = useSelector((state: AppState) => state.permissions);
    const [loadingError, setLoadingError] = useState<CustomGraphQLError | null>(null);
    const [activeRole, setActiveRole] = useState('admin');

    const roles: IMvpRole[] = [
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
        {
            id: 'hr',
            label: 'HR',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'back-to-categories',
            label: 'Zurück zu Kategorien',
            icon: getAppIcon(EIconCategory.CATEGORIES),
            onClick: () => navigate(`/${ELinks.SKILLCATEGORIES}`),
            variant: 'primary',
        },
        {
            id: 'skills-catalog',
            label: 'Fähigkeiten Katalog',
            icon: getAppIcon(EIconCategory.CATALOG),
            onClick: () => navigate(`/${ELinks.SKILLS}`),
            variant: 'default' as const,
        },
    ];

    const setEntityState = (ent: SkillCategory) => ent && dispatch(updateSkillCategory(ent));

    useEffect(() => {
        if (props.inDialog || entityId === -1) return;
        getSkillCategory(entityId).catch((ex) => {
            setLoadingError(ex);
        });
    }, [entityId, props.inDialog]);

    useEffect(() => {
        if (props.entity) setEntityState(props.entity);
    }, [props.entity]);
    const navigate = useNavigate();

    const skills = allSkills.filter((s) => s.categoryId === entityId);
    const handleOpenEditDialog = async () => {
        if (!entityState) return;
        return openDialog(<SkillCategoryDialog id="SkillCategoryDialog" skillCategory={entityState} />);
    };
    const subCategories = skillCategories.filter((sc) => sc.parentCategory === entityState?.id);
    return (
        <>
            {!props.inDialog && (
                <MvpSubNav
                    currentPageTitle={entityState?.title || 'Kategorie'}
                    roles={roles}
                    activeRole={activeRole}
                    onRoleChange={setActiveRole}
                    quickActions={quickActions}
                />
            )}
            <EntityPage
                inDialog={props.inDialog}
                admin={permissions.admin}
                permissions={permissions.entities.SkillCategories}
                loadingError={loadingError}
                entity={entityState}
                onDelete={() => {
                    if (!props.inDialog) navigate(`/${ELinks.SKILLCATEGORIES}/`);
                }}
                openEditDialog={handleOpenEditDialog}
                informations={
                    entityState
                        ? [
                              entityState.parentCategory ? (
                                  <EntityPageInformation title={langStrings.parentCategory}>
                                      <SkillCategoryBreadCrumb parentCategory={entityState.parentCategory} />
                                  </EntityPageInformation>
                              ) : undefined,
                              entityState.description ? (
                                  <EntityPageInformation title={langStrings.description} size={'full'}>
                                      <ExpandableText>
                                          <FormatedTextInput value={entityState.description} readonly={true} />
                                      </ExpandableText>
                                  </EntityPageInformation>
                              ) : undefined,
                          ]
                        : []
                }
            >
                <>
                    {subCategories.length > 0 && (
                        <SkillCategoriesCard title={langStrings.subCategories} skillCategories={subCategories} />
                    )}
                    <SkillsCard skills={skills} />
                    {entityState && entityState.employees !== null && (
                        <EmployeesCard
                            employees={entityState.employees.map((e) => ({
                                ...e,
                                count: e.skillCount,
                                level: e.averageSkillLevel,
                            }))}
                        />
                    )}
                </>
            </EntityPage>
        </>
    );
}

export default SkillCategoryPage;

interface SkillCategoryBreadCrumbProps {
    parentCategory: number | null;
}

/**
 * SkillCategoryBreadCrumb component.
 *
 * @param {SkillCategoryBreadCrumbProps} parentCategory - The parent category to display in the breadcrumb.
 * @returns {JSX.Element} The SkillCategoryBreadCrumb component.
 */
function SkillCategoryBreadCrumb({ parentCategory }: SkillCategoryBreadCrumbProps) {
    const skillCategories = useSelector((state: AppState) => state.company.skillCategories);
    const path = buildCategoryPath(skillCategories, parentCategory);

    if (path.length === 0) return null;
    // <SkillCategorySelect skillCategories={skillCategories} selectedCategory={entityState.parentCategory} readonly={true} />

    return (
        <Breadcrumbs aria-label="breadcrumb">
            {path.map((category) => (
                <Link
                    key={category.id}
                    to={entityTypeToLink(category.id, EEntityType.SKILL_CATEGORY)}
                    style={{ textDecoration: 'none' }}
                >
                    <Typography variant="body2">{category.title}</Typography>
                </Link>
            ))}
        </Breadcrumbs>
    );
}

/**
 * Builds the category path for a specific category ID.
 *
 * @param {SkillCategory[]} skillCategories - An array of skill categories.
 * @param {number} categoryId - The ID of the category to build the path for.
 * @returns {SkillCategory[]} - An array of skill categories representing the category path.
 */
function buildCategoryPath(skillCategories: SkillCategory[], categoryId: number | null): SkillCategory[] {
    // Map zur schnelleren Zuordnung von id zu SkillCategory erstellen
    const categoryMap = new Map<number, SkillCategory>();
    skillCategories.forEach((category) => categoryMap.set(category.id, category));

    const path: SkillCategory[] = [];
    let currentCategoryId = categoryId;

    while (currentCategoryId) {
        const currentCategory = categoryMap.get(currentCategoryId);
        if (!currentCategory) break; // Beendet die Schleife, wenn keine Kategorie gefunden wird

        path.unshift(currentCategory); // Fügt die aktuelle Kategorie am Anfang des Pfades hinzu
        currentCategoryId = currentCategory.parentCategory; // Setzt die Suche mit der Elternkategorie fort
    }

    return path;
}
